/* eslint-disable no-console */
'use client';

import { useCallback, useEffect, useRef } from 'react';
import { safePostHogCapture } from 'utils/posthogHelpers';

const safePostHog = {
  capture: (eventName, properties) => {
    try {
      if (typeof window !== 'undefined' && window.posthog) {
        window.posthog.capture(eventName, properties);
        return true;
      }
      return safePostHogCapture(eventName, properties);
    } catch (error) {
      console.warn('[PostHog] Capture error:', error);
      return false;
    }
  },
};

export const extractBuilderABTestData = (content) => {
  if (!content) return null;

  return {
    contentId: content.id || null,
    contentName: content.name || null,
    testVariationId: content.testVariationId || null,
    testVariationName: content.testVariationName || null,
    isInABTest: !!content.testVariationId,
    modelName: content.modelName || 'campaign-page',
    slug: content.data?.slug || null,
    publishedDate: content.lastUpdated || content.createdDate || null,
  };
};

export const trackBuilderImpression = (content, additionalData = {}) => {
  const abTestData = extractBuilderABTestData(content);

  if (!abTestData) {
    console.warn('[Builder] No content to track impression');
    return false;
  }

  // Fire PostHog $experiment_started for A/B tests (PostHog is the single source of truth)
  // Builder.io native tracking disabled to reduce duplicate API calls and Visual View costs
  if (abTestData.isInABTest) {
    safePostHog.capture('$experiment_started', {
      $experiment_id: `builder_${abTestData.contentId}`,
      $experiment_name: abTestData.contentName,
      $variant_id: abTestData.testVariationId,
      $variant_name: abTestData.testVariationName || 'default',
      experiment_source: 'builder.io',
      builder_slug: abTestData.slug,
      ...additionalData,
    });
  }

  return true;
};

export const trackBuilderClick = (content, elementId, additionalData = {}) => {
  const abTestData = extractBuilderABTestData(content);

  if (!abTestData) return false;

  safePostHog.capture('builder_content_click', {
    builder_content_id: abTestData.contentId,
    builder_variation_id: abTestData.testVariationId,
    element_id: elementId,
    ...additionalData,
  });

  return true;
};

export const trackBuilderConversionWithPostHog = (
  content,
  conversionGoal,
  value,
  additionalData = {},
) => {
  const abTestData = extractBuilderABTestData(content);

  if (!abTestData) return false;

  // Single PostHog event with goal as property (avoids unbounded event name cardinality)
  safePostHog.capture('builder_conversion', {
    builder_content_id: abTestData.contentId,
    builder_variation_id: abTestData.testVariationId,
    builder_variation_name: abTestData.testVariationName,
    conversion_goal: conversionGoal,
    conversion_value: value,
    is_ab_test: abTestData.isInABTest,
    ...additionalData,
  });

  return true;
};

export const useBuilderPostHogIntegration = (content, options = {}) => {
  const { trackImpressionOnMount = true, offeringSlug = null, additionalData = {} } = options;

  const hasTrackedImpression = useRef(false);
  const abTestData = extractBuilderABTestData(content);

  useEffect(() => {
    if (trackImpressionOnMount && content && !hasTrackedImpression.current) {
      trackBuilderImpression(content, {
        offering_slug: offeringSlug,
        ...additionalData,
      });
      hasTrackedImpression.current = true;
    }
  }, [content, trackImpressionOnMount, offeringSlug, additionalData]);

  const trackClick = useCallback(
    (elementId, clickData = {}) => {
      return trackBuilderClick(content, elementId, {
        offering_slug: offeringSlug,
        ...additionalData,
        ...clickData,
      });
    },
    [content, offeringSlug, additionalData],
  );

  const trackConversion = useCallback(
    (conversionGoal, value = null, conversionData = {}) => {
      return trackBuilderConversionWithPostHog(content, conversionGoal, value, {
        offering_slug: offeringSlug,
        ...additionalData,
        ...conversionData,
      });
    },
    [content, offeringSlug, additionalData],
  );

  const trackInvestClick = useCallback(
    (investData = {}) => {
      trackClick('invest_button', investData);
      return trackConversion('invest_clicked', null, investData);
    },
    [trackClick, trackConversion],
  );

  const trackInvestStarted = useCallback(
    (amount, investData = {}) => {
      return trackConversion('invest_started', amount, investData);
    },
    [trackConversion],
  );

  const trackInvestCompleted = useCallback(
    (amount, investData = {}) => {
      return trackConversion('invest_completed', amount, investData);
    },
    [trackConversion],
  );

  const trackSectionView = useCallback(
    (sectionName, sectionData = {}) => {
      return trackConversion('section_viewed', null, {
        section_name: sectionName,
        ...sectionData,
      });
    },
    [trackConversion],
  );

  const trackScrollDepth = useCallback(
    (depthPercent, scrollData = {}) => {
      return trackConversion('scroll_depth', depthPercent, {
        depth_percent: depthPercent,
        ...scrollData,
      });
    },
    [trackConversion],
  );

  const trackVideoEngagement = useCallback(
    (action, videoId, videoData = {}) => {
      return trackConversion('video_engagement', null, {
        video_action: action,
        video_id: videoId,
        ...videoData,
      });
    },
    [trackConversion],
  );

  return {
    abTestData,
    contentId: abTestData?.contentId,
    variationId: abTestData?.testVariationId,
    variationName: abTestData?.testVariationName,
    isInABTest: abTestData?.isInABTest || false,

    trackClick,
    trackConversion,
    trackInvestClick,
    trackInvestStarted,
    trackInvestCompleted,
    trackSectionView,
    trackScrollDepth,
    trackVideoEngagement,

    trackImpression: () =>
      trackBuilderImpression(content, {
        offering_slug: offeringSlug,
        ...additionalData,
      }),
  };
};

export default useBuilderPostHogIntegration;
